/**
 * Parses agent message strings for Shopify product/checkout blocks.
 *
 * Supported block formats:
 *   [PRODUCT_CARD:{...json...}]
 *   [CHECKOUT_LINK:{...json...}]
 */

export interface ProductCardData {
  product_id: string;
  title: string;
  price: string;
  image?: string;
  variants?: string;
  handle?: string;
  checkout_url?: string;
  /** True when the shown image is a default/hero color, not the color the
   *  shopper asked for. Drives a subtle "may show a default color" note. */
  color_image_fallback?: boolean;
}

export interface CheckoutLinkData {
  url: string;
  total: string;
  items: string;
}

export type ProductSegment =
  | { type: 'text'; content: string }
  | { type: 'product_card'; data: ProductCardData }
  | { type: 'checkout_link'; data: CheckoutLinkData };

const BLOCK_RE = /\[(PRODUCT_CARD|CHECKOUT_LINK):(\{[^[\]]*\})\]/g;

export function parseProductBlocks(message: string): ProductSegment[] {
  const segments: ProductSegment[] = [];
  let lastIndex = 0;

  for (const match of message.matchAll(BLOCK_RE)) {
    const start = match.index!;
    if (start > lastIndex) {
      segments.push({ type: 'text', content: message.slice(lastIndex, start) });
    }

    const blockType = match[1] === 'PRODUCT_CARD' ? 'product_card' : 'checkout_link';
    try {
      const data = JSON.parse(match[2]);
      segments.push({ type: blockType, data } as ProductSegment);
    } catch {
      segments.push({ type: 'text', content: match[0] });
    }

    lastIndex = start + match[0].length;
  }

  if (lastIndex < message.length) {
    segments.push({ type: 'text', content: message.slice(lastIndex) });
  }

  if (segments.length === 0) {
    return [{ type: 'text', content: message }];
  }

  return segments;
}
